import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plane, Car, BatteryCharging, Factory, Cpu, ArrowRight, Calculator, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'БПЛА', img: '/categories/drone.jpg', title: 'Запчастини для БПЛА', icon: Plane, glow: 'rgba(6,182,212,0.55)' },
  { key: 'Автозапчастини', img: '/categories/auto.jpg', title: 'Автозапчастини', icon: Car, glow: 'rgba(251,146,60,0.55)' },
  { key: 'Зарядні станції', img: '/categories/power.jpg', title: 'Зарядні станції та акумулятори', icon: BatteryCharging, glow: 'rgba(52,211,153,0.55)' },
  { key: 'Промислове обладнання', img: '/categories/industrial.jpg', title: 'Промислове обладнання', icon: Factory, glow: 'rgba(250,204,21,0.55)' },
  { key: 'Електроніка', img: '/categories/electronics.jpg', title: 'Електроніка та гаджети', icon: Cpu, glow: 'rgba(129,140,248,0.55)' },
];

const openLeadForm = () => window.dispatchEvent(new Event('open-lead-popup'));

export const WhatWeShip = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const [weight, setWeight] = useState('');
  const [contact, setContact] = useState('');
  const [sending, setSending] = useState(false);

  const activate = (i) => {
    if (openIdx !== i) { setWeight(''); setContact(''); }
    setOpenIdx(i);
  };
  const close = (e) => { if (e) e.stopPropagation(); setOpenIdx(null); };

  const submit = async (e, cat) => {
    e.preventDefault();
    e.stopPropagation();
    if (!contact.trim() || contact.trim().length < 3) {
      toast.error('Вкажіть телефон або Telegram');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quick-lead`, { category: cat, weight: weight.trim(), contact: contact.trim() });
      toast.success('Заявку відправлено! Менеджер зв\'яжеться з вами найближчим часом 🎉');
      setWeight(''); setContact(''); setOpenIdx(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Помилка відправки');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="relative py-14 md:py-20 bg-[#060a13]" data-testid="what-we-ship">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300 text-xs font-semibold uppercase tracking-wider mb-4">
            Наша спеціалізація
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Що ми <span className="text-orange-400">возимо</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg mt-4 max-w-2xl mx-auto">
            Доставляємо з Китаю товари будь-якої складності. Наведіть на категорію — і залиште швидку заявку на розрахунок.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {CATEGORIES.map((c, i) => {
            const Icon = c.icon;
            const isOpen = openIdx === i;
            return (
              <div
                key={i}
                data-testid={`ship-category-${i}`}
                onMouseEnter={() => activate(i)}
                onMouseLeave={() => setOpenIdx(null)}
                onClick={() => activate(i)}
                className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0a1224] cursor-pointer transition-all duration-300 hover:border-orange-400/60"
                style={{ boxShadow: isOpen ? `0 0 34px -6px ${c.glow}` : 'none' }}
              >
                {/* image + zoom + blur when open */}
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.title}
                    loading="lazy"
                    className={`w-full h-full object-cover transition-all duration-500 ease-out ${isOpen ? 'blur-md scale-110' : 'group-hover:scale-105'}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1224] via-[#0a1224]/30 to-transparent" />
                  <span className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-[#0a1224]/80 backdrop-blur-sm border border-white/15 flex items-center justify-center z-10">
                    <Icon className="w-5 h-5 text-orange-300" />
                  </span>
                </div>

                {/* footer title */}
                <div className="p-4 flex items-center justify-between gap-3">
                  <h3 className="text-white font-bold text-base sm:text-lg leading-tight">{c.title}</h3>
                  <span className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center opacity-80 group-hover:opacity-100 transition-all">
                    <ArrowRight className="w-4 h-4 text-[#1a0a00]" />
                  </span>
                </div>

                {/* Glassmorphism quick-form panel */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  className={`absolute inset-0 z-20 flex flex-col justify-center gap-2.5 p-4 sm:p-5 bg-[#0a1224]/55 backdrop-blur-md border border-white/15 transition-all duration-[400ms] ease-out ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                >
                  <button
                    type="button"
                    onClick={close}
                    className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white/80 hover:bg-white/20 sm:hidden"
                    data-testid={`ship-panel-close-${i}`}
                    aria-label="Закрити"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-white" />
                    </span>
                    <p className="text-white font-bold text-sm leading-tight">{c.title}</p>
                  </div>
                  <input
                    type="text"
                    value={isOpen ? weight : ''}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="Орієнтовна вага вантажу (кг)"
                    className="w-full h-10 px-3 rounded-lg bg-white/10 border border-white/25 text-white text-sm placeholder:text-white/50 focus:outline-none focus:border-orange-400 focus:bg-white/15 transition-colors"
                    data-testid={`ship-weight-${i}`}
                  />
                  <input
                    type="text"
                    value={isOpen ? contact : ''}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder="Ваш номер телефону / Telegram"
                    className="w-full h-10 px-3 rounded-lg bg-white/10 border border-white/25 text-white text-sm placeholder:text-white/50 focus:outline-none focus:border-orange-400 focus:bg-white/15 transition-colors"
                    data-testid={`ship-contact-${i}`}
                  />
                  <button
                    type="button"
                    onClick={(e) => submit(e, c.key)}
                    disabled={sending}
                    className="w-full h-11 rounded-full font-extrabold text-[#1a0a00] bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 shadow-[0_0_20px_rgba(251,146,60,0.5)] hover:shadow-[0_0_32px_rgba(251,146,60,0.8)] hover:scale-[1.02] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid={`ship-submit-${i}`}
                  >
                    <Calculator className="w-4 h-4" />
                    {sending ? 'Відправка...' : 'Розрахувати зараз'}
                  </button>
                </div>
              </div>
            );
          })}

          {/* CTA tile (last) — opens full lead form */}
          <button
            type="button"
            onClick={openLeadForm}
            data-testid="ship-category-cta"
            className="group relative rounded-2xl border border-orange-500/40 bg-gradient-to-br from-orange-500/15 to-[#0a1224] flex flex-col items-center justify-center text-center p-6 min-h-[220px] transition-all duration-300 hover:border-orange-400 hover:shadow-[0_0_34px_-6px_rgba(251,146,60,0.55)]"
          >
            <span className="w-14 h-14 rounded-2xl bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
              <ArrowRight className="w-7 h-7 text-[#1a0a00]" />
            </span>
            <h3 className="text-white font-bold text-lg mb-1">Не знайшли свій товар?</h3>
            <p className="text-slate-400 text-sm">Залиште заявку — розрахуємо вартість будь-якого замовлення</p>
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhatWeShip;
